<template>
  <base-layout>
    <h1 slot="header">
      {{ `app.pages.settings.printing` | trans }}
    </h1>
    <a slot="buttons" :href="routes.settings" class="btn btn-primary">
      <i class="fa fa-arrow-circle-left" aria-hidden="true"></i>
      {{ `app.back` | trans }}
    </a>
    <v-app id="vuetify-app">
      <v-container fluid>
        <v-alert :value="true" :type="`info`"
                 v-html="`<span>` + trans('app.settings.to_update_logo') + `</span>`">
        </v-alert>
        <h4>{{ `app.settings.print_settings`| trans }}</h4>
        <v-form v-model="printSettingsValid" ref="printSettings">
          <v-layout row wrap>
            <v-flex sm6>
              <v-text-field
                  v-model="printSettings.clinic"
                  :label="`app.clinic` | trans"
                  :rules="rules.clinic"
                  solo
              ></v-text-field>
            </v-flex>
            <v-flex sm6>
              <v-checkbox
                  class="ml-4"
                  v-model="printSettings.configuration.clinic"
                  :label="`app.show_on_print`|trans"
                  :color="`primary`"
              ></v-checkbox>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex sm6>
              <v-text-field
                  v-model="printSettings.phone"
                  :label="`app.phone_number` | trans"
                  :rules="rules.phone"
                  solo
              ></v-text-field>
            </v-flex>
            <v-flex sm6>
              <v-checkbox
                  class="ml-4"
                  v-model="printSettings.configuration.phone"
                  :label="`app.show_on_print`|trans"
                  :color="`primary`"
              ></v-checkbox>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex sm6>
              <v-text-field
                  v-model="printSettings.email"
                  :label="`app.email`|trans"
                  :rules="rules.email"
                  solo
              ></v-text-field>
            </v-flex>
            <v-flex sm6>
              <v-checkbox
                  class="ml-4"
                  v-model="printSettings.configuration.email"
                  :label="`app.show_on_print`|trans"
                  :color="`primary`"
              ></v-checkbox>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex sm6>
              <v-text-field
                  v-model="printSettings.website"
                  :label="`app.website`|trans"
                  :rules="rules.website"
                  solo
              ></v-text-field>
            </v-flex>
            <v-flex sm6>
              <v-checkbox
                  class="ml-4"
                  v-model="printSettings.configuration.website"
                  :label="`app.show_on_print`|trans"
                  :color="`primary`"
              ></v-checkbox>
            </v-flex>
          </v-layout>
        </v-form>
        <v-btn class="pull-right" color="primary" @click="updatePrintSettings()" :loading="printSettingsIsLoading" :disabled="!printSettingsValid">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-container>
      <v-divider></v-divider>
      <v-container fluid>
        <h4>{{ `app.settings.email_settings.header`| trans }}</h4>
        <v-form v-model="sendEmailSettingsValid" ref="emailSettings">
          <v-layout row wrap>
            <v-flex xs12>
              <v-text-field
                  v-model="emailSettings.title"
                  :label="'app.settings.email_settings.title' | trans"
                  :rules="rules.emailTitle"
                  solo
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-textarea
                  :label="'app.settings.email_settings.content' | trans"
                  v-model="emailSettings.content"
                  :rules="rules.emailContent"
                  full
                  rows="10"
                  solo
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-form>
        <v-btn
            class="pull-right"
            color="primary"
            :loading="sendEmailSettingsIsLoading"
            :disabled="!sendEmailSettingsValid"
            @click="updateEmailSettings()"
        >
          {{ `app.save` | trans }}
        </v-btn>
      </v-container>
      <v-divider></v-divider>
      <nutrients-form ref="nutrientsForm" :title="trans('app.settings.nutrients_print')" :max-nutrients="10" :route="'/settings/nutrients_print'" :permission="'settings_nutrients_print'"></nutrients-form>
    </v-app>
  </base-layout>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {trans} from '../../app';
import RulesBuilder from "../../utils/rulesBuilder";
import axios from "axios";

export default {
  props: {
    routes: Object
  },
  data: () => ({
    ruleBuilder: new RulesBuilder(),
    printSettingsValid: false,
    printSettingsIsLoading: false,
    sendEmailSettingsValid: true,
    sendEmailSettingsIsLoading: false,
    printSettings: {
      configuration: {
        clinic: false,
        phone: false,
        email: false,
        website: false,
      }
    },
    emailSettings: {
      title: '',
      content: '',
    },
  }),
  computed: {
    ...mapState(['dictionaries']),
    rules() {
      return {
        emailTitle: this.ruleBuilder
            .allowEmptyRule()
            .addMaxLengthRule(trans('app.settings.email_settings.title'), 255)
            .getRules(),
        emailContent: this.ruleBuilder
            .allowEmptyRule()
            .addMaxLengthRule(trans('app.settings.email_settings.content'), 10000)
            .getRules(),
        clinic: [
          v => (this.printSettings.configuration.clinic ? !!v : true) || trans('app.settings.validation.printing.clinic.required'),
        ],
        phone: [
          v => (this.printSettings.configuration.phone ? !!v : true) || trans('app.settings.validation.printing.phone.required'),
        ],
        email: [
          v => (this.printSettings.configuration.email ? !!v : true) || trans('app.settings.validation.printing.email.required'),
          v => (!!v ? /.+@.+/.test(v) : true) || trans('app.settings.validation.printing.email.valid'),
        ],
        website: [
          v => (this.printSettings.configuration.website ? !!v : true) || trans('app.settings.validation.printing.website.required'),
        ],
      }
    },
    dragOptions() {
      return {
        animation: 0,
        group: "description",
        disabled: false,
      };
    },
    ...mapGetters([
      'hasPermission',
    ]),
  },
  methods: {
    ...mapActions(['fetchSettings']),
    async updateEmailSettings() {
      this.$refs.emailSettings.validate();
      if (!this.sendEmailSettingsValid) {
        return;
      }
      this.sendEmailSettingsIsLoading = true;

      try {
        await axios.put('/settings/email_settings', this.emailSettings);
        this.$toast.success(trans('app.actions.saved', {name: trans('app.pages.settings.index').toLowerCase()}))
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.$toast.error(trans('app.save_failed'));
        } else {
          this.sentryCaptureException(error);
          this.$toast.error(trans('app.error_occurred'));
        }
      }
      this.sendEmailSettingsIsLoading = false;
    },
    async updatePrintSettings() {
      this.$refs.printSettings.validate();

      if (!this.printSettingsValid) {
        return;
      }

      this.printSettingsIsLoading = true;

      try {
        await axios.put('/settings/print_settings', this.printSettings);
        this.$toast.success(trans('app.actions.saved', {name: trans('app.pages.settings.index').toLowerCase()}));
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.$toast.error(trans('app.save_failed'));
        } else {
          this.sentryCaptureException(error);
        }
      }
      this.printSettingsIsLoading = false;
    },
  },
  created() {
    this.fetchSettings()
        .then(async () => {
          this.printSettings = this.$store.state.settings.print_settings;
          this.emailSettings = this.$store.state.settings.email_settings;
          await this.$refs.nutrientsForm.setCurrentNutrients(this.$store.state.settings.nutrients_print)
        }).catch((error) => {
      this.sentryCaptureException(error);
    });
  },
}
</script>
